import { map, get, isNumber, sumBy, flow, isEmpty, find } from 'lodash';
import { map as fpMap, join as fpJoin } from 'lodash/fp';
import { pipe, ifElse, always, path, unless, prop } from 'ramda';
import moment from 'moment-timezone';
import { Map } from 'immutable';
import {
  formatDuration,
  generateUrlString,
  formatHours,
  transformShopLocation,
} from '../utils/helpers';
import * as barberSelectors from '../barbers/selectors';
import { ANY_BARBER, TIP_NONE } from '../constants';
import {
  getScheduleWeekdaysOffFromSchedule,
  getWholeDaysOff,
} from '../utils/schedule';
import config from '../../config';
import {
  getNoExtrasForReserveNoPay,
  getShopAllowBookAndReserve,
  getShopBookNoPay,
} from '../shops/selectors';
import { getSelectedServicesPrepaid } from '../services/selectors';

export const getIsFormValid = state =>
  state.reservation.get('fields').every(field => field.get('touched') && !field.get('error'));

export const getIsFormPristine = state =>
  state.reservation.get('fields').every(field => !field.get('touched') && !field.get('value'));

export const getUser = state =>
  state.reservation.get('fields')
    .reduce((result, field) => {
      const user = { ...result };
      user[field.get('id')] = field.get('value') || '';
      return user;
    }, {});

export const getPromoAmount = (state, serviceCost, taxes) =>
  Math.min(
    state.reservation.getIn(['promo', 'amount']),
    serviceCost + taxes,
  );

export const getTotalMembershipDiscount = (state) => {
  const membershipsDiscounts = state.reservation.get('membershipDiscounts');
  return sumBy(membershipsDiscounts, 'amount');
};

export const getGiftCardBalance = state => state.reservation.getIn(['giftCard', 'remainingAmount']);

export const getGiftCardAmount = state => state.reservation.getIn(['giftCard', 'appliedAmount']);

export const getTotalAmount = state => state.reservation.get('total');
export const getTotalAmountWithoutTipAndFees = state => state.reservation.get('totalWithoutTipAndFees');

export const getIsValidatingAppointment = state =>
  state.reservation.get('outstandingAppointmentValidateRequests').size > 0;

export const getTimezone = state => state.reservation.getIn(['appointment', 'shop', 'timezone']);

export const getAppointmentDateTime = state => state.reservation.getIn(['appointment', 'dateTime']);
export const getAppointmentTimeInUrlFormat = state => moment(state.reservation.getIn(['appointment', 'dateTime']))
  .format(config.timeSlugFormat);

export const getAppointmentStart = state => moment.tz(getAppointmentDateTime(state), getTimezone(state));

const getAppointmentService = state => state.reservation.getIn(['appointment', 'appointment_service']);
const getAppointmentConfCode = state => state.reservation.getIn(['appointment', 'confirmationCode']);

export const getAppointment = (state) => {
  const duration = state.reservation.getIn(['appointment', 'duration']);
  const start = getAppointmentStart(state);

  return {
    formatted: start.format(config.dateTimeFormatLong),
    start: start.format(),
    formattedDuration: formatDuration(duration),
    duration,
    appointment_service: getAppointmentService(state),
    confirmationCode: getAppointmentConfCode(state),
  };
};

export const getAppointmentId = state => state.reservation.getIn(['appointment', 'id']);
export const isAppointmentPaid = state => !!state.reservation.getIn(['appointment', 'paymentStatus']);
export const getBarberName = (state) => {
  const firstName = state.reservation.getIn(['appointment', 'barber', 'firstName']);
  const lastName = state.reservation.getIn(['appointment', 'barber', 'lastName']);

  return `${firstName || ''} ${lastName || ''}`;
};

export const getServiceName = pipe(
  state => state.reservation.getIn(['appointment', 'appointment_service']),
  ifElse(
    isEmpty,
    always(''),
    pipe(
      item => item.toJS(),
      fpMap('name'),
      fpJoin(', ')
    )
  ),
);

export const getShop = pipe(
  state => state.reservation.getIn(['appointment', 'shop']),
  ifElse(
    isEmpty,
    always({}),
    pipe(
      item => item.toJS(),
      transformShopLocation,
    )
  ),
);

export const getShopLogo = pipe(
  getShop,
  path(['logo', 'url']),
);

export const getShopImage = pipe(
  getShop,
  path(['photos', 0, 'url']),
);

export const getShopObject = (state) => {
  const shop = state.reservation.getIn(['appointment', 'shop']);
  return shop && shop.toJS();
};

export const getShopBrandId = (state) => {
  const brandId = state.reservation.getIn(['appointment', 'shop', 'brandId']);
  return brandId;
};

export const getShopName = state => state.reservation.getIn(['appointment', 'shop', 'name']);

export const getShopId = state => state.reservation.getIn(['appointment', 'shop', 'id']);

export const getShopTimezone = state => state.reservation.getIn(['appointment', 'shop', 'timezone']);

export const getCustomer = (state) => {
  const id = state.reservation.getIn(['appointment', 'customer', 'id']);
  const firstName = state.reservation.getIn(['appointment', 'customer', 'firstName']);
  const lastName = state.reservation.getIn(['appointment', 'customer', 'lastName']);
  const name = firstName && lastName && `${firstName} ${lastName}`;
  const email = state.reservation.getIn(['appointment', 'customer', 'email']);

  return { name, email, id };
};

export const getBarberAdvanceCancelMins = state =>
  state.reservation.getIn(['appointment', 'barber', 'advanceCancelMins']);

export const getCanCustomerCancel = state =>
  state.reservation.getIn(['appointment', 'barber', 'canCustomerCancel']);

export const getAppointmentTime = state => state.reservation.getIn(['appointment', 'dateTime']);

export const getAppointmentInFuture = (state) => {
  const dateTime = getAppointmentTime(state);
  const timezone = state.reservation.getIn(['appointment', 'shop', 'timezone']);

  return moment.tz(dateTime, timezone).isAfter(moment());
};

export const getBarberAdvanceBookDays = state =>
  state.reservation.getIn(['appointment', 'barber', 'advanceBookDays']);

export const getAppointmentCanceled = state => state.reservation.getIn(['appointment', 'status']) === 1;

export const getAppointmentShop = state => state.reservation.getIn(['appointment', 'shop']);
export const getShopRoute = state => state.reservation.getIn(['appointment', 'shop', 'route']);
export const getBarberRoute = state => state.reservation.getIn(['appointment', 'barber', 'route']);
export const getServiceId = state => state.reservation.getIn(['appointment', 'appointment_service', 0, 'serviceId']);
export const getServiceIds = state => (state.reservation.getIn(['appointment', 'appointment_service']) || new Map())
  .map(as => as.getIn(['serviceId'])).join(';');
export const canBeChargedWithSavedCard = state => state.reservation.getIn(['appointment', 'canBeChargedWithSavedCard']);

const getBarberDaysOff = (state) => {
  const daysOff = state.reservation.getIn(['appointment', 'barber', 'days_off']);
  if (!daysOff || !daysOff.size) return [];
  return getWholeDaysOff(daysOff.toJS());
};

const mapDaysStringToDate = days => map(days, item =>
  moment(item, config.apiDateFormat).toDate());
export const getBarberDaysOffAsDates = flow(
  getBarberDaysOff,
  mapDaysStringToDate,
);

const getBarberDaysOn = (state) => {
  const daysOn = state.reservation.getIn(['appointment', 'barber', 'days_on']);
  if (!daysOn || !daysOn.size) return [];
  return daysOn.toJS();
};

export const getBarberDaysOnAsDates = flow(
  getBarberDaysOn,
  items => map(items, item => get(item, 'day')),
  mapDaysStringToDate,
);

const getBarberSchedules = (state) => {
  const schedules = state.reservation.getIn(['appointment', 'barber', 'schedules']);
  return schedules && schedules.toJS();
};

export const getBarberWeekdaysOff = flow(
  getBarberSchedules,
  getScheduleWeekdaysOffFromSchedule,
);

export const getHasCustomTip = state => state.reservation.get('customTip') > 0;
export const getCustomTip = state => state.reservation.get('customTip');

export const getTips = state => [...state.reservation.get('tips'), TIP_NONE].map((tip) => {
  const isNoTip = isNumber(tip);
  const selectedTip = state.reservation.get('tip');

  if (isNoTip) {
    return {
      value: tip,
      desc: 'tip in cash',
      amount: 0,
      active: getCustomTip(state) === 0 || tip === selectedTip,
    };
  }

  if (tip.flatAmount) {
    return {
      amount: tip.flatAmount,
      active: tip.flatAmount === selectedTip,
      flat: true,
    };
  }

  return {
    value: tip.percentage,
    desc: `about ${tip.percentage}%`,
    amount: tip.amount,
    active: tip.percentage === selectedTip,
  };
});
export const getActiveTip = (state) => {
  const customTip = state.reservation.get('customTip');
  if (customTip) {
    return {
      value: 0,
      desc: 'Custom tip',
      amount: customTip,
    };
  }

  return find(getTips(state), tip => tip.active) || null;
};
export const getActiveTipAmount = (state) => {
  const activeTip = getActiveTip(state);

  return activeTip ? activeTip.amount : 0;
};

export const getServicesCost = state => sumBy(state.reservation.get('services'), service => service.amount) || 0;

export const getPromoCode = state => state.reservation.getIn(['promo', 'promoCode']);

export const getBarberId = state => state.reservation.getIn(['appointment', 'barber', 'id']);

export const getServices = state => state.reservation.getIn(['appointment', 'appointment_service']);

export const getServiceDuration = state => flow(
  getServices,
  item => item && item.toJS(),
  items => sumBy(items, 'duration')
)(state);

export const getBookedWithAnyBarber = state => state.reservation.getIn(['appointment', 'bookedWithAnyBarber']);

export const getRebookLink = state => generateUrlString({
  shopRoute: getShopRoute(state),
  barberRoute: getBookedWithAnyBarber(state)
    ? ANY_BARBER
    : getBarberRoute(state),
  serviceId: getServiceId(state),
});

export const getScheduleLink = (state) => {
  const shopRoute = getShopRoute(state);
  const url = generateUrlString({ shopRoute });
  const bookingUrl = `/book${url}`;

  const shop = getAppointmentShop(state);
  if (!shop || !shop.toJS().allowBookWithAnyBarber) {
    return `${bookingUrl}/barber`;
  }

  return bookingUrl;
};

export const getBookAndPayCopy = (
  state,
  shopRoute,
  barberRoute,
  serviceIds,
  totalAmount,
  isCoveredByDiscount,
  isReserveNoPayOnly
) => {
  const isServicePrepayRequired = !getSelectedServicesPrepaid(state, serviceIds);
  const allowBookAndReserve = getShopAllowBookAndReserve(state, shopRoute) && !isServicePrepayRequired;
  const allowBookNoPay = getShopBookNoPay(state, shopRoute) && !isServicePrepayRequired;
  const advanceCancelMins = formatHours(barberSelectors.getBarberAdvanceCancelMins(state, barberRoute));
  const noExtrasForBookNoPay = getNoExtrasForReserveNoPay(state, shopRoute);
  const disclaimer = [
    'You will not be charged should you cancel/reschedule your appointment',
    `${advanceCancelMins} before your appointment is scheduled to begin.`,
  ];

  if (!noExtrasForBookNoPay && isCoveredByDiscount) {
    return '';
  }

  const reserveWithCardCopy = [
    'By selecting "Reserve With Card" you will be able to attach a payment to your',
    'reservation, a convenient way to save time on the day of your service.',
    'The payment will not process at the time of the booking, but will process',
    'at the time of your service.',
  ];
  const reserveOnlyCopy = [
    'By selecting "Reserve" you are authorizing the shop to charge for your appointment.',
  ];

  if (isReserveNoPayOnly) {
    return reserveOnlyCopy.concat(disclaimer).join(' ');
  }

  const hasTotalAndShowsTips = totalAmount > 0 || noExtrasForBookNoPay;
  if (allowBookAndReserve) {
    const reserveAndProvideCardCopy = [
      'If you select “Reserve” you will still',
      'be required to provide your credit/debit card info. You will only be charged if you',
      'do not show up to your appointment.',
    ];
    if (hasTotalAndShowsTips) {
      const reserveAndPayCopy = [
        'If you select "Reserve & Pay" your payment will automatically',
        'process at the time of your service, saving you and your professional',
        'time on the day of your service.',
      ];
      return reserveAndPayCopy.concat(reserveAndProvideCardCopy).concat(disclaimer).join(' ');
    }
    return reserveAndProvideCardCopy.concat(disclaimer).join(' ');
  }

  if (hasTotalAndShowsTips && allowBookNoPay) {
    return reserveWithCardCopy.concat(reserveOnlyCopy).concat(disclaimer).join(' ');
  }
  if (hasTotalAndShowsTips) {
    return reserveWithCardCopy.concat(disclaimer).join(' ');
  }
  return reserveOnlyCopy.concat(disclaimer).join(' ');
};

export const getItems = pipe(
  state => state.reservation.getIn(['appointment', 'items']),
  unless(isEmpty, items => items.toJS()),
);

export const getTaxAmount = state => state.reservation.getIn(['appointment', 'taxAmount']);

export const getDiscountPromo = state => state.reservation.getIn(['appointment', 'discountPromo']);

export const getTipAmount = state => state.reservation.getIn(['appointment', 'tipAmount']);

export const getTotalWithTip = state => state.reservation.getIn(['appointment', 'totalWithTip']);

export const getCustomerEmail = pipe(
  getCustomer,
  prop('email'),
);

export const getIsPaid = state => state.reservation.getIn(['appointment', 'isPaid']);

export const getPaymentProcessing = state => state.reservation.get('paymentProcessing');

export const getProcessing = state => state.reservation.get('processing');

export const getTaxBreakdown = state => state.reservation.get('taxBreakdown').toJS();
export const getTaxBreakdownAmount = state => sumBy(getTaxBreakdown(state), b => b.amount);



// WEBPACK FOOTER //
// ./src/app/reservation/selectors.js